import { useRef, useState } from 'react'
import type { ReactNode } from 'react'
import {
  useSharedElementTransition,
  type SharedElementTransitionController,
} from '../useSharedElementTransition'
import { adjacentIn, getAdjacentFinalizedGameweek } from '../season'
import type { GameweekDetail, GameweekResult } from '../types'

export interface LeagueNavigationController {
  /** The league whose detail screen is open, or null for the tab shell. */
  selectedLeagueId: string | null
  open: (leagueId: string) => void
  close: () => void
  transition: SharedElementTransitionController
}

/**
 * Which league detail is open, and the shared-element flight that opens it.
 * The two are one hook because the controller flips `selectedLeagueId` itself —
 * that is what lets a flight straddle the list→detail render swap.
 *
 * Scroll rules (§Scroll): the detail always mounts at the top, and the landing scroll is captured
 * on enter and restored on return. Source and destination rects are viewport-relative (the clone
 * is `position: fixed`), so scrolling to the top *before* the destination is measured is what
 * keeps the flight correct.
 */
export function useLeagueNavigation(): LeagueNavigationController {
  const [selectedLeagueId, setSelectedLeagueId] = useState<string | null>(null)
  const landingScrollRef = useRef(0)

  const transition = useSharedElementTransition({
    onEnter: (leagueId) => {
      landingScrollRef.current = window.scrollY
      setSelectedLeagueId(leagueId)
      window.scrollTo(0, 0)
    },
    onExit: () => {
      setSelectedLeagueId(null)
      const y = landingScrollRef.current
      requestAnimationFrame(() => window.scrollTo(0, y))
    },
  })

  return {
    selectedLeagueId,
    open: setSelectedLeagueId,
    close: () => setSelectedLeagueId(null),
    transition,
  }
}

/** Whose gameweek the results modal is showing, when it isn't the signed-in user's own. */
export interface HistoryOwner {
  userId: string
  displayName: string
  /** Their sharpest gameweek, or null when it isn't known. The eyebrow compares it against the
   *  week actually on screen, so stepping away from it drops the crown instead of carrying it. */
  bestGameweek: number | null
}

export interface GameweekHistoryController {
  /** The gameweek whose results modal is open, or null. */
  viewingHistoryGameweek: number | null
  /** Set only while the open modal belongs to another player. Null means the
   *  signed-in user's own gameweek, which is the only case that reads `seasonDetails`. */
  viewingHistoryOwner: HistoryOwner | null
  /**
   * The gameweeks the nav arrows step through, when the modal was opened from something with its
   * own set — a multi-charge chip tile walks that chip's uses, another player's sharpest week
   * walks their finalized season. Null means the default: every finalized gameweek of *your*
   * season.
   */
  viewingHistoryWalk: number[] | null
  /** Open a gameweek's results, flying the tapped dropdown row into the modal heading when the
   *  caller hands over a source to fly from. */
  select: (
    gameweek: number,
    source?: { anchorEl: HTMLElement | null; clone: ReactNode }
  ) => void
  /** Open one of *another player's* gameweeks with no walk — their picks are fetched on demand,
   *  and the nav arrows are off, so it is a single-week view. */
  selectForPlayer: (gameweek: number, owner: HistoryOwner) => void
  /**
   * Open a gameweek with the arrows bound to `walk` rather than to the season. Used by the
   * profile's chip tiles (the tile face leads with the chip's best week, and the arrows are what
   * reach its other uses) and by another player's sharpest week, whose walk is their finalized
   * season.
   */
  selectWalk: (gameweek: number, walk: number[], owner?: HistoryOwner) => void
  /** Step to the adjacent *finalized* gameweek, staying put when there is none. */
  step: (direction: 'previous' | 'next') => void
  close: () => void
  transition: SharedElementTransitionController
}

/**
 * The gameweek results modal and its shared-element entrance —
 * a second instance of the same controller the leagues list uses, flying the tapped dropdown row's
 * name + points block into the modal's heading. Dismissal is the modal's own (no reverse
 * collapse), so `beginBack` just tears the transition down and unmounts it.
 */
export function useGameweekHistory(
  seasonHistory: GameweekResult[],
  seasonDetails: Record<number, GameweekDetail>
): GameweekHistoryController {
  const [viewingHistoryGameweek, setViewingHistoryGameweek] = useState<number | null>(null)
  const [viewingHistoryOwner, setViewingHistoryOwner] = useState<HistoryOwner | null>(null)
  const [viewingHistoryWalk, setViewingHistoryWalk] = useState<number[] | null>(null)

  const openOwn = (gameweek: number | null) => {
    setViewingHistoryOwner(null)
    setViewingHistoryWalk(null)
    setViewingHistoryGameweek(gameweek)
  }

  const transition = useSharedElementTransition({
    onEnter: (gameweek) => openOwn(Number(gameweek)),
    onExit: () => openOwn(null),
  })

  return {
    viewingHistoryGameweek,
    viewingHistoryOwner,
    viewingHistoryWalk,
    transition,
    close: () => openOwn(null),

    selectForPlayer: (gameweek, owner) => {
      // No shared-element flight: there is no dropdown row to fly from, and the panel just fades
      // in the way every non-picker entry point does.
      setViewingHistoryOwner(owner)
      setViewingHistoryWalk(null)
      setViewingHistoryGameweek(gameweek)
    },

    selectWalk: (gameweek, walk, owner) => {
      setViewingHistoryOwner(owner ?? null)
      // A single-use chip has nothing to step to, so it keeps the plain single-week view.
      setViewingHistoryWalk(walk.length > 1 ? [...walk].sort((a, b) => a - b) : null)
      setViewingHistoryGameweek(gameweek)
    },

    select: (gameweek, source) => {
      // No stored detail means the modal renders its "no detail available" fallback — it has no
      // heading to fly into, so open it plainly rather than starting a flight that can never land.
      if (!source || !seasonDetails[gameweek]) {
        openOwn(gameweek)
        return
      }
      transition.activate(String(gameweek), source.anchorEl, {
        content: source.clone,
        className: 'gameweek-heading-clone',
      })
    },

    step: (direction) =>
      setViewingHistoryGameweek((prev) => {
        if (prev === null) return prev
        if (viewingHistoryWalk) return adjacentIn(viewingHistoryWalk, prev, direction) ?? prev
        return getAdjacentFinalizedGameweek(seasonHistory, prev, direction) ?? prev
      }),
  }
}

